import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Barcode,
  CalendarDays,
  Camera,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  Download,
  Eye,
  FileText,
  ImagePlus,
  Layers3,
  Link2,
  MapPin,
  Minus,
  Package,
  Paperclip,
  Phone,
  Plus,
  ReceiptText,
  Save,
  Search,
  ShoppingBag,
  Sparkles,
  Trash2,
  User,
  UserPlus,
  WalletCards,
  Wrench,
  X,
} from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

type DocumentType =
  | 'Orçamento'
  | 'Pedido'
  | 'Recibo'
  | 'Ordem de serviço'
  | 'Proposta comercial'
  | 'Cotação'
  | 'Estimativa'
  | 'Plano de trabalho'
  | 'Comprovante';

type QuoteStatus = 'Pendente' | 'Aprovado' | 'Concluído' | 'Cancelado';
type ItemType = 'service' | 'product';
type DiscountType = 'value' | 'percent';

interface QuoteItem {
  id: number;
  type: ItemType;
  name: string;
  description: string;
  category: string;
  sku: string;
  barcode: string;
  unit: string;
  trackStock: boolean;
  minimumStock: number;
  serviceCode: string;
  issRate: number;
  unitCost: number;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountType: DiscountType;
  photos: number;
  saveToCatalog: boolean;
}

const documentTypes: DocumentType[] = [
  'Orçamento',
  'Pedido',
  'Recibo',
  'Ordem de serviço',
  'Proposta comercial',
  'Cotação',
  'Estimativa',
  'Plano de trabalho',
  'Comprovante',
];

const statuses: QuoteStatus[] = ['Pendente', 'Aprovado', 'Concluído', 'Cancelado'];

const paymentMethods = [
  'Pix',
  'Dinheiro',
  'Cartão de crédito',
  'Cartão de débito',
  'Boleto',
  'Transferência',
];

const paymentSuggestions = [
  'À vista',
  '50% na entrada e 50% na entrega',
  'Até 3x sem juros',
  '30 dias após a entrega',
];

const pdfTemplates = [
  { id: 'classico', label: 'Clássico', detail: 'Direto e compacto' },
  { id: 'moderno', label: 'Moderno', detail: 'Destaques em verde' },
  { id: 'essencial', label: 'Essencial', detail: 'Leitura simplificada' },
];

const statusStyles: Record<QuoteStatus, string> = {
  Pendente: 'border-status-pending/40 bg-status-pending/10 text-status-pending',
  Aprovado: 'border-status-approved/40 bg-status-approved/10 text-status-approved',
  Concluído: 'border-status-completed/40 bg-status-completed/10 text-status-completed',
  Cancelado: 'border-status-cancelled/40 bg-status-cancelled/10 text-status-cancelled',
};

const initialItems: QuoteItem[] = [
  {
    id: 1,
    type: 'service',
    name: 'Instalação e acabamento',
    description: 'Preparação, instalação e limpeza final do ambiente.',
    category: 'Mão de obra',
    sku: 'SERV-001',
    barcode: '',
    unit: 'Serviço',
    trackStock: false,
    minimumStock: 0,
    serviceCode: '14.01',
    issRate: 5,
    unitCost: 420,
    quantity: 1,
    unitPrice: 1280,
    discount: 0,
    discountType: 'value',
    photos: 0,
    saveToCatalog: false,
  },
  {
    id: 2,
    type: 'product',
    name: 'Kit de materiais',
    description: 'Materiais e insumos previstos para a execução.',
    category: 'Materiais',
    sku: 'MAT-014',
    barcode: '7890000000142',
    unit: 'Kit',
    trackStock: true,
    minimumStock: 3,
    serviceCode: '',
    issRate: 0,
    unitCost: 410,
    quantity: 1,
    unitPrice: 740,
    discount: 5,
    discountType: 'percent',
    photos: 1,
    saveToCatalog: true,
  },
];

const today = new Date().toISOString().slice(0, 10);

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
}

function itemDiscount(item: QuoteItem) {
  const gross = item.quantity * item.unitPrice;
  return item.discountType === 'percent'
    ? gross * Math.min(item.discount, 100) / 100
    : Math.min(item.discount, gross);
}

const surfaceClass = 'rounded-[26px] border border-border/70 bg-card shadow-xl shadow-primary/[0.06]';
const fieldClass = 'h-11 rounded-xl border-border/80 bg-background/80 focus-visible:ring-primary';

export default function TestNewQuotePage3() {
  const navigate = useNavigate();
  const [documentType, setDocumentType] = useState<DocumentType>('Orçamento');
  const [status, setStatus] = useState<QuoteStatus>('Pendente');
  const [title, setTitle] = useState('Reforma da cozinha');
  const [date, setDate] = useState(today);
  const [showSequence, setShowSequence] = useState(true);
  const [sequenceId, setSequenceId] = useState('0001');
  const [clientName, setClientName] = useState('Mariana Costa');
  const [clientSelected, setClientSelected] = useState(true);
  const [clientPhone, setClientPhone] = useState('(11) 98765-4321');
  const [clientAddress, setClientAddress] = useState('Rua das Palmeiras, 240');
  const [clientDocument, setClientDocument] = useState('123.456.789-00');
  const [saveClient, setSaveClient] = useState(true);
  const [items, setItems] = useState<QuoteItem[]>(initialItems);
  const [showAdjustments, setShowAdjustments] = useState(true);
  const [shipping, setShipping] = useState(90);
  const [headerDiscount, setHeaderDiscount] = useState(100);
  const [headerDiscountType, setHeaderDiscountType] = useState<DiscountType>('value');
  const [validityDays, setValidityDays] = useState(15);
  const [warrantyAmount, setWarrantyAmount] = useState(90);
  const [warrantyUnit, setWarrantyUnit] = useState('dias');
  const [freeWarranty, setFreeWarranty] = useState(false);
  const [warrantyText, setWarrantyText] = useState('Garantia sobre o serviço executado');
  const [selectedPayments, setSelectedPayments] = useState(['Pix', 'Cartão de crédito']);
  const [paymentTerms, setPaymentTerms] = useState('50% na entrada e 50% na entrega');
  const [reference, setReference] = useState('REF-2026-018');
  const [notes, setNotes] = useState('Prazo estimado de execução: 5 dias úteis.');
  const [terms, setTerms] = useState('Materiais adicionais serão cobrados após aprovação do cliente.');
  const [pdfTemplate, setPdfTemplate] = useState('moderno');
  const [generalPhotos, setGeneralPhotos] = useState(1);
  const [feedback, setFeedback] = useState('Rascunho pronto para edição');

  const totals = useMemo(() => {
    const gross = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    const itemDiscounts = items.reduce((sum, item) => sum + itemDiscount(item), 0);
    const afterItems = Math.max(gross - itemDiscounts, 0);
    const totalDiscount = headerDiscountType === 'percent'
      ? afterItems * Math.min(headerDiscount, 100) / 100
      : Math.min(headerDiscount, afterItems);
    const total = Math.max(afterItems - totalDiscount + shipping, 0);
    return { gross, itemDiscounts, totalDiscount, total };
  }, [headerDiscount, headerDiscountType, items, shipping]);

  const updateItem = <K extends keyof QuoteItem>(id: number, key: K, value: QuoteItem[K]) => {
    setItems((current) => current.map((item) => item.id === id ? { ...item, [key]: value } : item));
  };

  const addItem = (type: ItemType, source: string) => {
    const nextId = Math.max(0, ...items.map((item) => item.id)) + 1;
    setItems((current) => [
      ...current,
      {
        id: nextId,
        type,
        name: type === 'service' ? 'Novo serviço' : 'Novo produto',
        description: '',
        category: type === 'service' ? 'Serviços' : 'Produtos',
        sku: '',
        barcode: '',
        unit: type === 'service' ? 'Serviço' : 'Unidade',
        trackStock: false,
        minimumStock: 0,
        serviceCode: '',
        issRate: 0,
        unitCost: 0,
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        discountType: 'value',
        photos: 0,
        saveToCatalog: false,
      },
    ]);
    setFeedback(`${source}: item adicionado ao orçamento`);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    setItems((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setFeedback('Ordem dos itens atualizada');
  };

  const togglePayment = (method: string) => {
    setSelectedPayments((current) => current.includes(method)
      ? current.filter((item) => item !== method)
      : [...current, method]);
  };

  const copyPreviewLink = async () => {
    const link = `${window.location.origin}/o/exemplo-orcamento`;
    try {
      await navigator.clipboard.writeText(link);
      setFeedback('Link de demonstração copiado');
    } catch {
      setFeedback('Prévia do link preparada para compartilhamento');
    }
  };

  const inputNumber = (value: string) => Number.isFinite(Number(value)) ? Number(value) : 0;

  return (
    <MainLayout>
      <div className="-mx-4 -mt-6 overflow-hidden bg-gradient-to-b from-primary/[0.09] via-background to-background pb-8">
        <header className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-primary to-emerald-600 px-5 pb-20 pt-5 text-primary-foreground">
          <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-warning/90" />
          <div className="pointer-events-none absolute -bottom-16 -left-12 h-40 w-56 rotate-12 rounded-[48px] bg-white/10" />
          <div className="pointer-events-none absolute left-1/3 top-20 h-28 w-28 rotate-45 rounded-[30px] border border-white/10" />

          <div className="relative flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Voltar"
              className="rounded-full text-white hover:bg-white/15 hover:text-white"
              onClick={() => navigate('/quotes/new')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold">
              <Sparkles className="h-3.5 w-3.5 text-warning" />
              EasyTravel · versão 3
            </div>
          </div>

          <div className="relative mt-8 max-w-sm">
            <p className="text-sm font-medium text-white/75">Novo documento</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight">Crie um orçamento memorável</h1>
            <p className="mt-3 text-sm leading-6 text-white/80">
              Organize cliente, itens e condições em uma jornada simples — do rascunho ao envio.
            </p>
          </div>

          <div className="relative mt-7 grid grid-cols-3 gap-2">
            <div className="rounded-2xl border border-white/15 bg-white/10 p-3">
              <Package className="mb-2 h-4 w-4 text-warning" />
              <p className="text-lg font-bold">{items.length}</p>
              <p className="text-[11px] text-white/70">itens</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-3">
              <Clock3 className="mb-2 h-4 w-4 text-warning" />
              <p className="text-lg font-bold">{validityDays}d</p>
              <p className="text-[11px] text-white/70">validade</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-3">
              <CircleDollarSign className="mb-2 h-4 w-4 text-warning" />
              <p className="truncate text-sm font-bold">{formatCurrency(totals.total)}</p>
              <p className="text-[11px] text-white/70">total</p>
            </div>
          </div>
        </header>

        <div className="relative -mt-12 space-y-4 px-3">
          <section className={cn(surfaceClass, 'p-5')} aria-labelledby="documento-title">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <div className="mb-1 flex items-center gap-2 text-primary">
                  <ReceiptText className="h-4 w-4" />
                  <p className="text-xs font-bold uppercase tracking-[0.16em]">Etapa 01</p>
                </div>
                <h2 id="documento-title" className="text-xl font-bold text-foreground">Dados do documento</h2>
              </div>
              <span className={cn('rounded-full border px-2.5 py-1 text-[11px] font-bold', statusStyles[status])}>
                {status}
              </span>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="document-type">Tipo de documento</Label>
                <select
                  id="document-type"
                  value={documentType}
                  onChange={(event) => setDocumentType(event.target.value as DocumentType)}
                  className={cn(fieldClass, 'w-full border px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30')}
                >
                  {documentTypes.map((type) => <option key={type}>{type}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote-title">Título opcional</Label>
                <Input id="quote-title" className={fieldClass} value={title} onChange={(event) => setTitle(event.target.value)} />
              </div>

              <div className="grid grid-cols-[1fr_0.9fr] gap-3">
                <div className="space-y-2">
                  <Label htmlFor="quote-date">Data</Label>
                  <div className="relative">
                    <CalendarDays className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <Input id="quote-date" type="date" className={cn(fieldClass, 'pl-10')} value={date} onChange={(event) => setDate(event.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sequence">Número</Label>
                  <Input id="sequence" className={fieldClass} value={sequenceId} onChange={(event) => setSequenceId(event.target.value)} />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-border/70 bg-muted/35 p-3">
                <div>
                  <p className="text-sm font-semibold">Exibir #{sequenceId || '0000'}</p>
                  <p className="text-xs text-muted-foreground">No PDF e no link público</p>
                </div>
                <Switch checked={showSequence} onCheckedChange={setShowSequence} aria-label="Exibir número no PDF e link" />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <div className="grid grid-cols-2 gap-2">
                  {statuses.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setStatus(option)}
                      className={cn(
                        'rounded-xl border px-3 py-2 text-xs font-semibold transition-colors',
                        status === option ? statusStyles[option] : 'border-border bg-background text-muted-foreground hover:border-primary/40',
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className={cn(surfaceClass, 'overflow-hidden')} aria-labelledby="cliente-title">
            <div className="bg-gradient-to-r from-primary/[0.12] to-accent px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Etapa 02</p>
                    <h2 id="cliente-title" className="font-bold">Cliente</h2>
                  </div>
                </div>
                {clientSelected && <Check className="h-5 w-5 text-primary" />}
              </div>
            </div>

            <div className="space-y-4 p-5">
              {clientSelected ? (
                <div className="rounded-2xl border border-primary/20 bg-primary/[0.05] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-bold">{clientName}</p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Phone className="h-3.5 w-3.5" />{clientPhone}</p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{clientAddress}</p>
                    </div>
                    <Button type="button" variant="ghost" size="icon" aria-label="Limpar seleção" className="h-8 w-8 rounded-full" onClick={() => { setClientSelected(false); setClientName(''); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline" className="rounded-xl border-primary/25 text-primary" onClick={() => setFeedback(`WhatsApp aberto para ${clientName}`)}>
                      <Phone className="mr-2 h-4 w-4" />WhatsApp
                    </Button>
                    <Button type="button" variant="outline" className="rounded-xl" onClick={() => setClientSelected(false)}>
                      Trocar cliente
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button type="button" className="h-11 w-full rounded-xl" onClick={() => { setClientName('Mariana Costa'); setClientSelected(true); setFeedback('Cliente localizado e selecionado'); }}>
                    <Search className="mr-2 h-4 w-4" />Buscar cliente
                  </Button>
                  <div className="relative flex items-center gap-3 py-1 text-xs text-muted-foreground before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">ou digite</div>
                  <div className="space-y-2">
                    <Label htmlFor="client-name">Nome do cliente *</Label>
                    <Input id="client-name" className={fieldClass} placeholder="Nome do cliente" value={clientName} onChange={(event) => setClientName(event.target.value)} />
                  </div>
                  {clientName.trim() && (
                    <div className="space-y-3 rounded-2xl border border-dashed border-primary/30 bg-accent/40 p-4">
                      <p className="flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4 text-primary" />Dados adicionais do cliente</p>
                      <Input className={fieldClass} placeholder="WhatsApp / telefone" value={clientPhone} onChange={(event) => setClientPhone(event.target.value)} />
                      <Input className={fieldClass} placeholder="Endereço" value={clientAddress} onChange={(event) => setClientAddress(event.target.value)} />
                      <Input className={fieldClass} placeholder="CPF / CNPJ" value={clientDocument} onChange={(event) => setClientDocument(event.target.value)} />
                      <Button type="button" variant="outline" className="w-full rounded-xl" onClick={() => setFeedback('Contato preparado para a agenda do dispositivo')}>
                        <UserPlus className="mr-2 h-4 w-4" />Salvar na agenda
                      </Button>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox checked={saveClient} onCheckedChange={(checked) => setSaveClient(checked === true)} />
                        Salvar este cliente na minha lista
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          <section className={cn(surfaceClass, 'p-5')} aria-labelledby="itens-title">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Etapa 03</p>
                <h2 id="itens-title" className="text-xl font-bold">Itens do orçamento</h2>
              </div>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">{items.length} itens</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button type="button" className="h-auto min-h-14 justify-start rounded-2xl px-3 text-left" onClick={() => addItem('product', 'Catálogo')}>
                <Package className="mr-2 h-4 w-4 shrink-0" /><span>Do catálogo</span>
              </Button>
              <Button type="button" variant="outline" className="h-auto min-h-14 justify-start rounded-2xl px-3 text-left" onClick={() => addItem('product', 'Código de barras')}>
                <Barcode className="mr-2 h-4 w-4 shrink-0" /><span>Ler código</span>
              </Button>
              <Button type="button" variant="outline" className="h-auto min-h-14 justify-start rounded-2xl px-3 text-left" onClick={() => addItem('service', 'Criação manual')}>
                <Wrench className="mr-2 h-4 w-4 shrink-0 text-status-completed" /><span>Criar serviço</span>
              </Button>
              <Button type="button" variant="outline" className="h-auto min-h-14 justify-start rounded-2xl px-3 text-left" onClick={() => addItem('product', 'Criação manual')}>
                <ShoppingBag className="mr-2 h-4 w-4 shrink-0 text-warning" /><span>Criar produto</span>
              </Button>
            </div>

            <div className="mt-5 space-y-3">
              {items.map((item, index) => (
                <article key={item.id} className="relative overflow-hidden rounded-[22px] border border-border bg-background shadow-sm">
                  <div className="absolute -left-2 top-20 h-4 w-4 rounded-full border-r border-border bg-card" />
                  <div className="absolute -right-2 top-20 h-4 w-4 rounded-full border-l border-border bg-card" />
                  <div className="flex items-center justify-between gap-2 bg-muted/45 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={cn('grid h-8 w-8 place-items-center rounded-xl', item.type === 'service' ? 'bg-status-completed/10 text-status-completed' : 'bg-warning/15 text-warning')}>
                        {item.type === 'service' ? <Wrench className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
                      </span>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Item {String(index + 1).padStart(2, '0')}</p>
                        <p className="text-xs font-semibold">{item.type === 'service' ? 'Serviço' : 'Produto'}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Button type="button" variant="ghost" size="icon" aria-label="Mover item para cima" className="h-8 w-8" disabled={index === 0} onClick={() => moveItem(index, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
                      <Button type="button" variant="ghost" size="icon" aria-label="Mover item para baixo" className="h-8 w-8" disabled={index === items.length - 1} onClick={() => moveItem(index, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
                      <Button type="button" variant="ghost" size="icon" aria-label="Remover item" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => { setItems((current) => current.filter((candidate) => candidate.id !== item.id)); setFeedback('Item removido'); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>

                  <div className="space-y-3 p-4">
                    <Input aria-label="Nome do item" className={cn(fieldClass, 'font-semibold')} value={item.name} onChange={(event) => updateItem(item.id, 'name', event.target.value)} />
                    <Textarea aria-label="Descrição do item" className="min-h-20 rounded-xl bg-background" placeholder="Descrição / detalhamento (opcional)" value={item.description} onChange={(event) => updateItem(item.id, 'description', event.target.value)} />

                    <div className="grid grid-cols-[0.9fr_1.1fr] gap-2">
                      <div className="space-y-1.5">
                        <Label htmlFor={`quantity-${item.id}`} className="text-xs">Quantidade</Label>
                        <div className="flex">
                          <Button type="button" variant="outline" size="icon" aria-label="Diminuir quantidade" className="h-10 w-9 rounded-r-none" disabled={item.quantity <= 1} onClick={() => updateItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}><Minus className="h-3 w-3" /></Button>
                          <Input id={`quantity-${item.id}`} type="number" min={1} step="0.5" className="h-10 rounded-none border-x-0 px-1 text-center" value={item.quantity} onChange={(event) => updateItem(item.id, 'quantity', Math.max(1, inputNumber(event.target.value)))} />
                          <Button type="button" variant="outline" size="icon" aria-label="Aumentar quantidade" className="h-10 w-9 rounded-l-none" onClick={() => updateItem(item.id, 'quantity', item.quantity + 1)}><Plus className="h-3 w-3" /></Button>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`price-${item.id}`} className="text-xs">Preço unitário</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-xs font-semibold text-muted-foreground">R$</span>
                          <Input id={`price-${item.id}`} type="number" min={0} step="0.01" className="h-10 rounded-xl pl-9" value={item.unitPrice} onChange={(event) => updateItem(item.id, 'unitPrice', inputNumber(event.target.value))} />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-[1fr_72px] gap-2">
                      <div className="space-y-1.5">
                        <Label htmlFor={`discount-${item.id}`} className="text-xs">Desconto do item</Label>
                        <Input id={`discount-${item.id}`} type="number" min={0} step="0.01" className="h-10 rounded-xl" value={item.discount} onChange={(event) => updateItem(item.id, 'discount', inputNumber(event.target.value))} />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`discount-type-${item.id}`} className="text-xs">Tipo</Label>
                        <select id={`discount-type-${item.id}`} className="h-10 w-full rounded-xl border border-border bg-background px-2 text-sm" value={item.discountType} onChange={(event) => updateItem(item.id, 'discountType', event.target.value as DiscountType)}>
                          <option value="value">R$</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-dashed border-border pt-3">
                      <p className="text-xs text-muted-foreground">Total do item</p>
                      <p className="text-lg font-extrabold text-primary">{formatCurrency(item.quantity * item.unitPrice - itemDiscount(item))}</p>
                    </div>

                    <details className="group rounded-xl border border-border/70 bg-muted/25 px-3 py-2">
                      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold">
                        Detalhes do item
                        <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                      </summary>
                      <div className="mt-3 space-y-3 border-t border-border/70 pt-3">
                        <div className="grid grid-cols-2 gap-2">
                          <Input aria-label="Categoria" className="h-10 rounded-xl" placeholder="Categoria" value={item.category} onChange={(event) => updateItem(item.id, 'category', event.target.value)} />
                          <Input aria-label="Código interno SKU" className="h-10 rounded-xl" placeholder="Código / SKU" value={item.sku} onChange={(event) => updateItem(item.id, 'sku', event.target.value)} />
                        </div>
                        <select aria-label="Unidade do item" className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm" value={item.unit} onChange={(event) => updateItem(item.id, 'unit', event.target.value)}>
                          <option>Serviço</option><option>Unidade</option><option>Hora</option><option>Dia</option><option>Metro</option><option>Kit</option>
                        </select>
                        {item.type === 'product' ? (
                          <div className="space-y-3 rounded-xl border border-border/70 bg-background p-3">
                            <div className="relative">
                              <Barcode className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input aria-label="Código de barras do produto" className="h-10 rounded-xl pl-10" placeholder="Código de barras" value={item.barcode} onChange={(event) => updateItem(item.id, 'barcode', event.target.value)} />
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <div><p className="text-xs font-semibold">Controlar estoque</p><p className="text-[10px] text-muted-foreground">Acompanhar quantidade disponível</p></div>
                              <Switch checked={item.trackStock} onCheckedChange={(checked) => updateItem(item.id, 'trackStock', checked)} />
                            </div>
                            {item.trackStock && <Input type="number" min={0} aria-label="Estoque mínimo" className="h-10 rounded-xl" placeholder="Estoque mínimo" value={item.minimumStock} onChange={(event) => updateItem(item.id, 'minimumStock', inputNumber(event.target.value))} />}
                          </div>
                        ) : (
                          <div className="grid grid-cols-[1fr_90px] gap-2 rounded-xl border border-border/70 bg-background p-3">
                            <Input aria-label="Serviço LC 116" className="h-10 rounded-xl" placeholder="Serviço (LC 116)" value={item.serviceCode} onChange={(event) => updateItem(item.id, 'serviceCode', event.target.value)} />
                            <div className="relative">
                              <Input type="number" min={0} aria-label="Alíquota ISS" className="h-10 rounded-xl pr-7" placeholder="ISS" value={item.issRate} onChange={(event) => updateItem(item.id, 'issRate', inputNumber(event.target.value))} />
                              <span className="pointer-events-none absolute right-3 top-2.5 text-xs text-muted-foreground">%</span>
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => { updateItem(item.id, 'photos', item.photos + 1); setFeedback('Foto anexada ao item'); }}>
                            <Camera className="mr-2 h-4 w-4" />Foto {item.photos ? `(${item.photos})` : ''}
                          </Button>
                          <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-xl border border-border px-3 text-xs font-medium">
                            <Checkbox checked={item.saveToCatalog} onCheckedChange={(checked) => updateItem(item.id, 'saveToCatalog', checked === true)} />
                            Salvar no catálogo
                          </label>
                        </div>
                        {item.saveToCatalog && (
                          <div className="space-y-1.5">
                            <Label htmlFor={`cost-${item.id}`} className="text-xs">Custo do item (opcional)</Label>
                            <Input id={`cost-${item.id}`} type="number" min={0} step="0.01" className="h-10 rounded-xl" value={item.unitCost} onChange={(event) => updateItem(item.id, 'unitCost', inputNumber(event.target.value))} />
                          </div>
                        )}
                      </div>
                    </details>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className={cn(surfaceClass, 'overflow-hidden')} aria-labelledby="total-title">
            <div className="bg-gradient-to-br from-emerald-800 via-primary to-emerald-600 p-5 text-primary-foreground">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/70">Resumo</p>
                  <h2 id="total-title" className="mt-1 text-xl font-bold">Total do orçamento</h2>
                </div>
                <WalletCards className="h-6 w-6 text-warning" />
              </div>
              <p className="mt-6 text-4xl font-extrabold tracking-tight">{formatCurrency(totals.total)}</p>
              <p className="mt-1 text-xs text-white/70">Valores atualizados automaticamente</p>
            </div>
            <div className="space-y-4 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Frete ou desconto no total</p>
                  <p className="text-xs text-muted-foreground">Ajustes gerais do documento</p>
                </div>
                <Switch checked={showAdjustments} onCheckedChange={setShowAdjustments} />
              </div>

              {showAdjustments && (
                <div className="grid grid-cols-2 gap-3 rounded-2xl bg-muted/35 p-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="shipping" className="text-xs">Frete (R$)</Label>
                    <Input id="shipping" type="number" min={0} className="h-10 rounded-xl" value={shipping} onChange={(event) => setShipping(inputNumber(event.target.value))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="total-discount" className="text-xs">Desconto</Label>
                    <div className="flex">
                      <Input id="total-discount" type="number" min={0} className="h-10 rounded-r-none" value={headerDiscount} onChange={(event) => setHeaderDiscount(inputNumber(event.target.value))} />
                      <select aria-label="Tipo de desconto total" className="h-10 w-14 rounded-r-xl border border-l-0 border-border bg-background px-1 text-sm" value={headerDiscountType} onChange={(event) => setHeaderDiscountType(event.target.value as DiscountType)}>
                        <option value="value">R$</option><option value="percent">%</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal dos itens</span><span>{formatCurrency(totals.gross)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Descontos nos itens</span><span>- {formatCurrency(totals.itemDiscounts)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Desconto no total</span><span>- {formatCurrency(totals.totalDiscount)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Frete</span><span>+ {formatCurrency(shipping)}</span></div>
                <div className="flex justify-between border-t border-dashed border-border pt-3 text-base font-extrabold"><span>Total</span><span className="text-primary">{formatCurrency(totals.total)}</span></div>
              </div>
            </div>
          </section>

          <section className={cn(surfaceClass, 'p-5')} aria-labelledby="extras-title">
            <div className="mb-5 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-accent text-primary"><Layers3 className="h-5 w-5" /></div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Etapa 04</p>
                <h2 id="extras-title" className="font-bold">Campos adicionais</h2>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="validity">Validade (dias)</Label>
                  <Input id="validity" type="number" min={0} className={fieldClass} value={validityDays} onChange={(event) => setValidityDays(inputNumber(event.target.value))} />
                </div>
                {!freeWarranty && (
                  <div className="space-y-2">
                    <Label htmlFor="warranty">Garantia</Label>
                    <div className="flex">
                      <Input id="warranty" type="number" min={0} className="h-11 rounded-r-none" value={warrantyAmount} onChange={(event) => setWarrantyAmount(inputNumber(event.target.value))} />
                      <select aria-label="Unidade da garantia" className="h-11 w-20 rounded-r-xl border border-l-0 border-border bg-background px-2 text-xs" value={warrantyUnit} onChange={(event) => setWarrantyUnit(event.target.value)}>
                        <option>dias</option><option>meses</option><option>anos</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => setFreeWarranty((current) => !current)}>
                {freeWarranty ? 'Usar garantia por período' : 'Escrever garantia em texto livre'}
              </button>
              {freeWarranty && <Textarea className="rounded-xl" value={warrantyText} onChange={(event) => setWarrantyText(event.target.value)} />}

              <div className="space-y-3 border-t border-border pt-5">
                <Label>Formas de pagamento aceitas</Label>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethods.map((method) => (
                    <label key={method} className={cn('flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-xs font-medium', selectedPayments.includes(method) ? 'border-primary/35 bg-accent text-primary' : 'border-border bg-background')}>
                      <Checkbox checked={selectedPayments.includes(method)} onCheckedChange={() => togglePayment(method)} />
                      {method}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-terms">Condições de pagamento</Label>
                <Textarea id="payment-terms" className="rounded-xl" value={paymentTerms} onChange={(event) => setPaymentTerms(event.target.value)} />
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {paymentSuggestions.map((suggestion) => (
                    <button key={suggestion} type="button" className="shrink-0 rounded-full border border-primary/20 bg-accent/60 px-3 py-1.5 text-[11px] font-semibold text-primary" onClick={() => setPaymentTerms(suggestion)}>{suggestion}</button>
                  ))}
                </div>
              </div>

              <div className="space-y-2"><Label htmlFor="reference">Referência / nº externo</Label><Input id="reference" className={fieldClass} value={reference} onChange={(event) => setReference(event.target.value)} /></div>
              <div className="space-y-2"><Label htmlFor="notes">Observações</Label><Textarea id="notes" className="rounded-xl" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
              <div className="space-y-2"><Label htmlFor="terms">Termos e condições / contrato</Label><Textarea id="terms" className="min-h-24 rounded-xl" value={terms} onChange={(event) => setTerms(event.target.value)} /></div>
            </div>
          </section>

          <section className={cn(surfaceClass, 'p-5')} aria-labelledby="pdf-title">
            <div className="mb-5 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground"><FileText className="h-5 w-5" /></div>
              <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Apresentação</p><h2 id="pdf-title" className="font-bold">Modelo do PDF</h2></div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {pdfTemplates.map((template) => (
                <button key={template.id} type="button" onClick={() => setPdfTemplate(template.id)} className={cn('relative overflow-hidden rounded-2xl border p-2 text-left transition-all', pdfTemplate === template.id ? 'border-primary bg-accent ring-2 ring-primary/15' : 'border-border bg-background')}>
                  <div className="mb-2 aspect-[3/4] rounded-lg border border-border bg-card p-1.5 shadow-sm">
                    <div className="h-2 rounded-sm bg-primary" />
                    <div className="mt-2 h-1 w-3/4 rounded bg-muted-foreground/30" />
                    <div className="mt-1 h-1 rounded bg-muted-foreground/20" />
                    <div className="mt-3 h-5 rounded bg-accent" />
                  </div>
                  <p className="text-xs font-bold">{template.label}</p>
                  <p className="mt-0.5 text-[9px] leading-3 text-muted-foreground">{template.detail}</p>
                  {pdfTemplate === template.id && <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground"><Check className="h-3 w-3" /></span>}
                </button>
              ))}
            </div>

            <div className="mt-5 rounded-2xl border border-dashed border-primary/30 bg-accent/35 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-card text-primary"><Paperclip className="h-4 w-4" /></div>
                  <div><p className="text-sm font-semibold">Fotos / anexos</p><p className="text-xs text-muted-foreground">{generalPhotos}/5 adicionados</p></div>
                </div>
                <Button type="button" variant="outline" size="sm" className="rounded-xl" disabled={generalPhotos >= 5} onClick={() => { setGeneralPhotos((count) => Math.min(5, count + 1)); setFeedback('Foto geral adicionada'); }}>
                  <ImagePlus className="mr-1.5 h-4 w-4" />Adicionar
                </Button>
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-primary/20 bg-gradient-to-br from-accent via-card to-card p-5 shadow-xl shadow-primary/[0.08]" aria-labelledby="acoes-title">
            <div className="mb-4 flex items-center justify-between">
              <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Finalizar</p><h2 id="acoes-title" className="text-xl font-bold">Seu orçamento está pronto</h2></div>
              <span className="grid h-10 w-10 place-items-center rounded-full bg-warning text-warning-foreground"><Check className="h-5 w-5" /></span>
            </div>

            <Button type="button" className="h-13 w-full rounded-2xl text-base font-bold shadow-lg shadow-primary/20" onClick={() => setFeedback(`${documentType} salvo com sucesso`)}>
              <Save className="mr-2 h-5 w-5" />Salvar orçamento
            </Button>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Button type="button" variant="outline" className="h-auto flex-col gap-1 rounded-2xl py-3 text-[11px]" onClick={() => setFeedback('PDF de demonstração preparado')}><Download className="h-4 w-4" />Baixar PDF</Button>
              <Button type="button" variant="outline" className="h-auto flex-col gap-1 rounded-2xl py-3 text-[11px]" onClick={copyPreviewLink}><Link2 className="h-4 w-4" />Copiar link</Button>
              <Button type="button" variant="outline" className="h-auto flex-col gap-1 rounded-2xl py-3 text-[11px]" onClick={() => setFeedback('Visualização pública aberta em modo de teste')}><Eye className="h-4 w-4" />Visualizar</Button>
            </div>
          </section>

          <div className="flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/[0.06] px-4 py-3 text-sm" role="status" aria-live="polite">
            <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground"><Check className="h-3.5 w-3.5" /></span>
            <div><p className="font-semibold text-foreground">{feedback}</p><p className="mt-0.5 text-xs text-muted-foreground">Ambiente visual de teste, sem salvar dados reais.</p></div>
          </div>

          <button type="button" className="mx-auto flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-muted-foreground" onClick={() => setFeedback('Você está em /test-novo-orcamento-3')}>
            <Copy className="h-3.5 w-3.5" />/test-novo-orcamento-3
          </button>
        </div>
      </div>
    </MainLayout>
  );
}
